import { PropsWithChildren, Suspense } from 'react'
import { Await, useLoaderData } from 'react-router-dom'

import FullPageLoader from './FullPageLoader'
import FullPageError from './FullPageError'

import { appConfig, withAngularJSCompatibility } from '$utils'

/**
 * Component that will ensure any wrapped children are rendered only after the
 * data required for the page has been loaded (based on React Router config),
 * a loading page is displayed in the interim.
 * @param props
 * @returns
 */
const PageLoadingWrapper = (props: PropsWithChildren) => {
  // This is the data that is returned by the React Router `loader` property.
  // It's something that needs to be have been wrapped in the react-router
  // `defer` function so that it is compatible with the `Await` component
  // we wrap the child component in. The `Await` component is responsible for
  // rendering the fallback loader up until the data is available - i.e.
  // when the promises deferred are all resolved.
  //
  // Note we're ignoring the type here as it's not possible to know what the
  // defered loader data is going to be now - see https://github.com/remix-run/react-router/discussions/9792
  // for discussion about how to type this safely.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data }: any = useLoaderData()

  return (
    <Suspense fallback={<FullPageLoader />}>
      <Await resolve={data} errorElement={<FullPageError />}>
        {props.children}
      </Await>
    </Suspense>
  )
}

const PageLoadingWithAngularJSCompatibility =
  withAngularJSCompatibility(PageLoadingWrapper)

type PageLoadingSupportProps = PropsWithChildren & {
  /**
   * Set this to true when the loader is nested within another loader.
   *
   * This ensures that we have a single React DOM tree, instead of
   * multiple sibling DOM elements, one for each loader. Any loader
   * that isn't declared as nested is rendered with AngularJS compatibility,
   * which results in the loader being rendered as a React Portal.
   */
  isNestedLoader?: boolean
}

/**
 * A wrapper component that is expected to encapsulate the page content on a
 * React Router route. It'll show a loading page until the data required is
 * available.
 * @param Component A React component representing the page content.
 * @returns
 */
const PageLoadingSupport = ({
  isNestedLoader: nestedLoader = false,
  children,
}: PageLoadingSupportProps) => {
  const isStandalone = appConfig.get('standalone') ?? false

  const WrapperComponent =
    isStandalone || nestedLoader
      ? PageLoadingWrapper
      : PageLoadingWithAngularJSCompatibility

  return <WrapperComponent>{children}</WrapperComponent>
}

export default PageLoadingSupport
